'use client';

import { Portal } from '@ark-ui/react';
import { IconButton, Popover, Tooltip } from '@terra/ui-design-system';
import { HStack } from '@terra/ui-styled-system';
import { Bug01Icon, FolderLibraryIcon, Message01Icon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import type { CoPilotDevToolId } from './useCoPilotDevTools';

type CoPilotDevToolsHubProps = {
  activeTool: CoPilotDevToolId | null;
  onToggleTool: (tool: CoPilotDevToolId) => void;
};

export function CoPilotDevToolsHub({
  activeTool,
  onToggleTool,
}: CoPilotDevToolsHubProps) {
  const t = useTranslations('chat.copilot.agentContext');
  const [isFlyoutOpen, setIsFlyoutOpen] = useState(false);
  const hasActiveTool = activeTool !== null;
  const options: {
    id: CoPilotDevToolId;
    label: string;
    icon: typeof FolderLibraryIcon;
  }[] = [
    {
      id: 'filesystem',
      icon: FolderLibraryIcon,
      label: t('filesystemHubLabel'),
    },
    {
      id: 'agent-messages',
      icon: Message01Icon,
      label: t('hubLabel'),
    },
  ];

  return (
    <Popover.Root
      open={isFlyoutOpen}
      onOpenChange={(details) => setIsFlyoutOpen(details.open)}
      positioning={{
        placement: 'top-start',
        strategy: 'fixed',
        gutter: 6,
        overflowPadding: 8,
      }}
      closeOnInteractOutside
    >
      <Popover.Trigger asChild>
        <IconButton
          type="button"
          variant="ghost"
          size="xs"
          aria-label={t('devToolsAriaLabel')}
          color={hasActiveTool || isFlyoutOpen ? 'fg.default' : 'fg.muted'}
          _hover={{ color: 'fg.default', bg: 'transparent' }}
        >
          <Bug01Icon size={16} />
        </IconButton>
      </Popover.Trigger>
      <Portal>
        <Popover.Positioner>
          <Popover.Content px={3} py={2} minW="auto">
            <HStack gap={2} alignItems="center">
              {options.map(({ id, label, icon: Icon }) => {
                const isActive = activeTool === id;

                return (
                  <Tooltip
                    key={id}
                    title={label}
                    positioning={{ strategy: 'fixed', placement: 'top' }}
                  >
                    <IconButton
                      type="button"
                      variant="ghost"
                      size="xs"
                      aria-label={label}
                      aria-pressed={isActive}
                      onClick={() => {
                        onToggleTool(id);
                        setIsFlyoutOpen(false);
                      }}
                      color={isActive ? 'fg.default' : 'fg.muted'}
                      _hover={{ color: 'fg.default', bg: 'bg.subtle' }}
                      px={1.5}
                      py={1}
                    >
                      <Icon size={14} />
                    </IconButton>
                  </Tooltip>
                );
              })}
            </HStack>
          </Popover.Content>
        </Popover.Positioner>
      </Portal>
    </Popover.Root>
  );
}
